import React, { useState, useMemo } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  AlertTriangle, 
  AlertCircle, 
  Info, 
  Lightbulb, 
  X, 
  ChevronDown, 
  ChevronRight,
  Clock,
  Calendar,
  Copy,
  FolderX,
  TrendingUp,
  AlertOctagon,
  Filter
} from 'lucide-react';
import type { AnomalyWarning } from '@/services/aiService';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface AnomalyAlertsPanelEnhancedProps {
  warnings: AnomalyWarning[];
  onDismiss?: (warningId: string) => void;
  onAutoFix?: (warningId: string) => void;
}

// Category definitions with metadata
const CATEGORY_CONFIG = {
  'policy-violations': {
    label: 'Policy Violations',
    icon: AlertOctagon,
    description: 'Critical compliance and policy issues',
    color: 'text-red-600',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
  },
  'time-tracking': {
    label: 'Time Tracking Issues',
    icon: Clock,
    description: 'Missing or incomplete time entries',
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200',
  },
  'approval-delays': {
    label: 'Approval & Workflow',
    icon: Calendar,
    description: 'Pending approvals and workflow issues',
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-50',
    borderColor: 'border-yellow-200',
  },
  'data-quality': {
    label: 'Data Quality',
    icon: Copy,
    description: 'Duplicate entries and data inconsistencies',
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
  },
  'patterns': {
    label: 'Patterns & Insights',
    icon: TrendingUp,
    description: 'Unusual patterns and optimization suggestions',
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
  },
  'project-issues': {
    label: 'Project Issues',
    icon: FolderX,
    description: 'Closed projects and project-related warnings',
    color: 'text-gray-600',
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-200',
  },
};

// Map warning types to categories
const TYPE_TO_CATEGORY: Record<AnomalyWarning['type'], keyof typeof CATEGORY_CONFIG> = {
  'excessive-hours': 'policy-violations',
  'overtime-risk': 'policy-violations',
  'weekend-work': 'policy-violations',
  'missing-time': 'time-tracking',
  'duplicate-entry': 'data-quality',
  'closed-project': 'project-issues',
  'unusual-pattern': 'patterns',
};

// Priority levels
type PriorityLevel = 'high' | 'medium' | 'low';

interface GroupedWarnings {
  category: keyof typeof CATEGORY_CONFIG;
  warnings: AnomalyWarning[];
  highestPriority: PriorityLevel;
}

export const AnomalyAlertsPanelEnhanced: React.FC<AnomalyAlertsPanelEnhancedProps> = ({
  warnings,
  onDismiss,
  onAutoFix,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set());
  const [priorityFilter, setPriorityFilter] = useState<Set<PriorityLevel>>(
    new Set(['high', 'medium', 'low'])
  );

  // Group warnings by category and priority
  const groupedWarnings = useMemo(() => {
    const groups = new Map<keyof typeof CATEGORY_CONFIG, AnomalyWarning[]>();

    warnings.forEach((warning) => {
      const category = TYPE_TO_CATEGORY[warning.type] || 'patterns';
      if (!groups.has(category)) {
        groups.set(category, []);
      }
      groups.get(category)!.push(warning);
    });

    // Convert to array and sort by priority
    const result: GroupedWarnings[] = Array.from(groups.entries()).map(([category, warnings]) => {
      // Sort warnings within category by severity
      const sortedWarnings = [...warnings].sort((a, b) => {
        const severityOrder = { high: 0, medium: 1, low: 2 };
        return severityOrder[a.severity] - severityOrder[b.severity];
      });

      // Determine highest priority in group
      const highestPriority = sortedWarnings[0]?.severity || 'low';

      return {
        category,
        warnings: sortedWarnings,
        highestPriority,
      };
    });

    // Sort groups by highest priority
    result.sort((a, b) => {
      const severityOrder = { high: 0, medium: 1, low: 2 };
      return severityOrder[a.highestPriority] - severityOrder[b.highestPriority];
    });

    return result;
  }, [warnings]);

  // Filter by priority
  const filteredGroups = useMemo(() => {
    return groupedWarnings
      .map((group) => ({
        ...group,
        warnings: group.warnings.filter((w) => priorityFilter.has(w.severity)),
      }))
      .filter((group) => group.warnings.length > 0);
  }, [groupedWarnings, priorityFilter]);

  const toggleCategory = (category: string) => {
    setCollapsedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  const togglePriorityFilter = (priority: PriorityLevel) => {
    setPriorityFilter((prev) => {
      const next = new Set(prev);
      if (next.has(priority)) {
        next.delete(priority);
      } else {
        next.add(priority);
      }
      return next;
    });
  };

  const getSeverityIcon = (severity: AnomalyWarning['severity']) => {
    switch (severity) {
      case 'high':
        return <AlertTriangle className="h-4 w-4" />;
      case 'medium':
        return <AlertCircle className="h-4 w-4" />;
      case 'low':
        return <Info className="h-4 w-4" />;
    }
  };

  const getSeverityColor = (severity: AnomalyWarning['severity']): 'destructive' | 'warning' | 'info' => {
    switch (severity) {
      case 'high':
        return 'destructive';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
    }
  };

  const getSeverityBadgeColor = (severity: AnomalyWarning['severity']) => {
    switch (severity) {
      case 'high':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'low':
        return 'bg-blue-100 text-blue-800 border-blue-300';
    }
  };

  const totalIssues = warnings.length;
  const highPriorityCount = warnings.filter((w) => w.severity === 'high').length;
  const mediumPriorityCount = warnings.filter((w) => w.severity === 'medium').length;
  const lowPriorityCount = warnings.filter((w) => w.severity === 'low').length;

  if (warnings.length === 0) return null;

  return (
    <div className="border border-primary/20 rounded-lg overflow-hidden">
      {/* Main Header - Always Visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between gap-3 p-3 bg-gradient-to-r from-primary/5 to-primary/10 hover:from-primary/10 hover:to-primary/15 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Lightbulb className="h-5 w-5 text-primary" />
          <div className="text-left">
            <h3 className="text-sm font-semibold">AI Insights</h3>
            <p className="text-xs text-muted-foreground">
              {totalIssues} {totalIssues === 1 ? 'issue' : 'issues'} detected
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Priority summary badges */}
          {highPriorityCount > 0 && (
            <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-xs">
              {highPriorityCount} High
            </Badge>
          )}
          {mediumPriorityCount > 0 && (
            <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200 text-xs">
              {mediumPriorityCount} Medium
            </Badge>
          )}
          {lowPriorityCount > 0 && (
            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-xs">
              {lowPriorityCount} Low
            </Badge>
          )}
          
          {/* Expand/Collapse indicator */}
          {isExpanded ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          )}
        </div>
      </button>

      {/* Collapsible Content */}
      {isExpanded && (
        <div className="p-3 space-y-3 bg-background">
          {/* Filter Controls - Inside Collapsible Section */}
          <div className="flex items-center justify-end">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 gap-2">
                  <Filter className="h-3.5 w-3.5" />
                  Filter by Priority
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel>Filter by Priority</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuCheckboxItem
                  checked={priorityFilter.has('high')}
                  onCheckedChange={() => togglePriorityFilter('high')}
                >
                  <span className="flex items-center gap-2">
                    <AlertTriangle className="h-3 w-3 text-red-600" />
                    High Priority
                  </span>
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={priorityFilter.has('medium')}
                  onCheckedChange={() => togglePriorityFilter('medium')}
                >
                  <span className="flex items-center gap-2">
                    <AlertCircle className="h-3 w-3 text-yellow-600" />
                    Medium Priority
                  </span>
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={priorityFilter.has('low')}
                  onCheckedChange={() => togglePriorityFilter('low')}
                >
                  <span className="flex items-center gap-2">
                    <Info className="h-3 w-3 text-blue-600" />
                    Low Priority
                  </span>
                </DropdownMenuCheckboxItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Grouped warnings */}
          <div className="space-y-2">
        {filteredGroups.map((group) => {
          const config = CATEGORY_CONFIG[group.category];
          const isCollapsed = collapsedCategories.has(group.category);
          const CategoryIcon = config.icon;

          return (
            <div
              key={group.category}
              className={cn(
                'border rounded-lg overflow-hidden transition-all',
                config.borderColor
              )}
            >
              {/* Category header */}
              <button
                onClick={() => toggleCategory(group.category)}
                className={cn(
                  'w-full flex items-center justify-between p-3 text-left transition-colors hover:opacity-80',
                  config.bgColor
                )}
              >
                <div className="flex items-center gap-3">
                  <CategoryIcon className={cn('h-4 w-4', config.color)} />
                  <div>
                    <h4 className={cn('text-sm font-semibold', config.color)}>
                      {config.label}
                    </h4>
                    <p className="text-xs text-muted-foreground">
                      {config.description}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs">
                    {group.warnings.length}
                  </Badge>
                  <Badge
                    variant="outline"
                    className={cn('text-xs', getSeverityBadgeColor(group.highestPriority))}
                  >
                    {group.highestPriority}
                  </Badge>
                  {isCollapsed ? (
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  )}
                </div>
              </button>

              {/* Category warnings */}
              {!isCollapsed && (
                <div className="p-2 space-y-2 bg-background">
                  {group.warnings.map((warning) => (
                    <Alert
                      key={warning.id}
                      variant={getSeverityColor(warning.severity)}
                      className="relative"
                    >
                      <div className="flex items-start gap-3">
                        {getSeverityIcon(warning.severity)}
                        <div className="flex-1 space-y-1">
                          <AlertTitle className="text-sm font-medium">
                            {warning.message}
                          </AlertTitle>
                          {warning.suggestion && (
                            <AlertDescription className="text-xs">
                              💡 {warning.suggestion}
                            </AlertDescription>
                          )}
                          {warning.autoFixAvailable && onAutoFix && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="mt-2 h-7 text-xs"
                              onClick={() => onAutoFix(warning.id)}
                            >
                              Auto-fix
                            </Button>
                          )}
                        </div>
                        {onDismiss && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 absolute top-2 right-2"
                            onClick={() => onDismiss(warning.id)}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </Alert>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

          {/* Empty state when all filtered out */}
          {filteredGroups.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <Info className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No issues match the selected filters</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
